import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { UserLite } from "utils/types/UserTypes";
import { GiftcardLite, ManualAssignPayload } from "utils/types";
import { useDebounce } from "utils/hooks/useDebounce";
import {
  dedupeById,
  dedupeByIdGiftCard,
  manualAssignGiftcard,
  searchAvailableGiftcards,
  searchUsers,
} from "utils";
import CheckIcon from "@mui/icons-material/Check";
type Props = {
  open: boolean;
  onClose: () => void;
  defaultUserId?: string;
  defaultGiftCardId?: string;
  onSuccess?: (data: {
    saleId: string;
    charged: number;
    giftCardId: string;
    userId: string;
  }) => void;
};

export default function ManualAssignGiftcardDialog({
  open,
  onClose,
  defaultUserId,
  defaultGiftCardId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = React.useState(false);
  type AlertState = {
    type: "success" | "error" | "info" | null;
    message: string;
  };
  const [alert, setAlert] = React.useState<AlertState>({
    type: null,
    message: "",
  });
  const [userInput, setUserInput] = React.useState("");
  const [giftInput, setGiftInput] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState<UserLite | null>(null);
  const [selectedGift, setSelectedGift] = React.useState<GiftcardLite | null>(
    null
  );
  const [chargeWallet, setChargeWallet] = React.useState(true);
  const [overridePriceUSD, setOverridePriceUSD] = React.useState<string>("");
  const [note, setNote] = React.useState("");
  const [userOptions, setUserOptions] = React.useState<UserLite[]>([]);
  const [giftOptions, setGiftOptions] = React.useState<GiftcardLite[]>([]);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [loadingGifts, setLoadingGifts] = React.useState(false);

  const debouncedUserQ = useDebounce(userInput, 300);
  const debouncedGiftQ = useDebounce(giftInput, 300);

  React.useEffect(() => {
    if (!open) return;
    setChargeWallet(true);
    setOverridePriceUSD("");
    setNote("");
  }, [open]);

  React.useEffect(() => {
    let active = true;
    (async () => {
      if (debouncedUserQ.trim().length < 1 && !defaultUserId) {
        setUserOptions((prev) =>
          selectedUser ? dedupeById([selectedUser, ...prev]) : prev
        );
        setLoadingUsers(false);
        return;
      }
      setLoadingUsers(true);
      try {
        const res = await searchUsers(debouncedUserQ || "");
        const list = res.data?.data ?? [];
        setUserOptions(
          dedupeById(selectedUser ? [selectedUser, ...list] : list)
        );
        if (defaultUserId && !selectedUser) {
          const found = list.find((u) => u.userID === defaultUserId);
          if (found) setSelectedUser(found);
        }
      } finally {
        if (active) setLoadingUsers(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [debouncedUserQ, defaultUserId, selectedUser]);

  React.useEffect(() => {
    let active = true;
    (async () => {
      if (debouncedGiftQ.trim().length < 1 && !defaultGiftCardId) {
        setGiftOptions((prev) =>
          selectedGift ? dedupeByIdGiftCard([selectedGift, ...prev]) : prev
        );
        setLoadingGifts(false);
        return;
      }
      setLoadingGifts(true);
      try {
        const res = await searchAvailableGiftcards(debouncedGiftQ || "");
        const list = res.data?.data ?? [];
        setGiftOptions(
          dedupeByIdGiftCard(selectedGift ? [selectedGift, ...list] : list)
        );
        if (defaultGiftCardId && !selectedGift) {
          const found = list.find((g) => g.id === defaultGiftCardId);
          if (found) setSelectedGift(found);
        }
      } finally {
        if (active) setLoadingGifts(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [debouncedGiftQ, defaultGiftCardId, selectedGift]);

  const handleSubmit = async () => {
    console.log(selectedUser)
    if (!selectedUser?.userID) {
      console.log("Sélectionne un utilisateur.");
      setAlert({ type: "error", message: "Sélectionne un utilisateur." });
      return;
    }
    if (!selectedGift?.id) {
      console.log("Sélectionne une carte cadeau disponible.");
      setAlert({
        type: "error",
        message: "Sélectionne une carte cadeau disponible.",
      });
      return;
    }
    const price = overridePriceUSD.trim()
      ? Number(overridePriceUSD)
      : undefined;
    if (price !== undefined && (Number.isNaN(price) || price < 0)) {
      setAlert({
        type: "error",
        message: "Le prix forcé doit être un nombre positif.",
      });
      return;
    }

    const payload: ManualAssignPayload = {
      giftCardId: selectedGift.id,
      chargeWallet,
      ...(price !== undefined
        ? { overridePriceUSD: Number(price.toFixed(2)) }
        : {}),
      ...(note ? { note } : {}),
    };

    setLoading(true);
    setAlert({ type: null, message: "" }); // clear
    try {
      const res = await manualAssignGiftcard(selectedUser.userID, payload);
      if (res.status === "success") {
        setAlert({ type: "success", message: "Carte assignée avec succès." });
        onSuccess?.(res.data!);
        onClose();
      } else {
        setAlert({
          type: "error",
          message: res?.message || "Échec de l’assignation.",
        });
      }
    } catch (e: any) {
      setAlert({ type: "error", message: e?.message || "Erreur réseau." });
    } finally {
      setLoading(false);
    }
  };



  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Attribuer une carte cadeau manuellement</DialogTitle>
      <DialogContent dividers>
        {alert.type && (
          <Alert
            sx={{ mb: 2 }}
            icon={
              alert.type === "success" ? (
                <CheckIcon fontSize="inherit" />
              ) : undefined
            }
            severity={alert.type}
            onClose={() => setAlert({ type: null, message: "" })}
          >
            {alert.message}
          </Alert>
        )}
        <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
          <Autocomplete
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            noOptionsText="Aucun résultat"
            onInputChange={(_, v) => setUserInput(v)}
            options={userOptions}
            getOptionLabel={(o) => o?.username ?? ""}
            isOptionEqualToValue={(opt, val) => opt?.userID === val?.userID}
            renderOption={(props, option) => {
              const { key, ...rest } =
                props as React.HTMLAttributes<HTMLLIElement> & {
                  key: React.Key;
                };
              return (
                <li key={key} {...rest}>
                  {option.username} {option.email ? `— ${option.email}` : ""}
                </li>
              );
            }}
            loading={loadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Utilisateur"
                placeholder="Rechercher par username / email"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            value={selectedGift}
            onChange={(_, v) => setSelectedGift(v)}
            onInputChange={(_, v) => setGiftInput(v)}
            options={giftOptions}
            noOptionsText="Aucun résultat"
            getOptionLabel={(o) =>
              o?.fullname
                ? `${o.fullname}${
                    o.faceValueUSD ? ` (${o.faceValueUSD} USD)` : ""
                  }`
                : ""
            }
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            renderOption={(props, option) => {
              const { key, ...rest } =
                props as React.HTMLAttributes<HTMLLIElement> & {
                  key: React.Key;
                };
              return (
                <li key={key} {...rest}>
                  {option.fullname}
                  {option.faceValueUSD ? ` (${option.faceValueUSD} USD)` : ""}
                </li>
              );
            }}
            loading={loadingGifts}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Carte cadeau disponible"
                placeholder="Rechercher par nom / ID"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingGifts ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={chargeWallet}
                onChange={(e) => setChargeWallet(e.target.checked)}
              />
            }
            label="Débiter le wallet de l'utilisateur"
          />

          <TextField
            label="Prix forcé (USD) – optionnel"
            value={overridePriceUSD}
            onChange={(e) => setOverridePriceUSD(e.target.value)}
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
          />

          <TextField
            label="Note (optionnel)"
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex: Rattrapage suite à lag"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button onClick={handleSubmit} disabled={loading || !selectedUser || !selectedGift}  variant="contained">
          {loading ? "Traitement..." : "Attribuer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


